import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useState, useRef, useEffect } from 'react';
import api from '../services/api';

export default function Header() {
    const navigate = useNavigate();
    const { pendingCount } = useNotifications();
    const { user, logout } = useAuth();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [notifications, setNotifications] = useState<any[]>([]);
    const menuRef = useRef<HTMLDivElement>(null);
    const notifRef = useRef<HTMLDivElement>(null);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
            if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
                setIsNotificationsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Get initials
    const getInitials = (name: string) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return name.substring(0, 2).toUpperCase();
    };

    // Format role
    const formatRole = (role: string) => {
        const roles: { [key: string]: string } = {
            'admin': 'Administrador',
            'operador': 'Operador',
            'tecnico': 'Técnico',
            'gestor': 'Gestor'
        };
        return roles[role] || role;
    };

    return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
            <div className="w-full mx-auto px-6 py-6">
                <div className="flex items-center justify-between">
                    {/* Logo and Title */}
                    <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
                        <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
                            <i className="ri-apps-2-fill text-xl text-white"></i>
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Hubb</h1>
                            <p className="text-xs text-gray-500">Onde tudo se conecta.</p>
                        </div>
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center space-x-6">
                        {/* Notifications */}
                        <div className="relative" ref={notifRef}>
                            <button
                                onClick={async () => {
                                    if (!isNotificationsOpen) {
                                        try {
                                            const res = await api.get('/api/notificacoes');
                                            if (res.data.status === 'success') {
                                                setNotifications(res.data.data);
                                            }
                                        } catch (err) { console.error(err); }
                                    }
                                    setIsNotificationsOpen(!isNotificationsOpen);
                                    setIsMenuOpen(false);
                                }}
                                className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                            >
                                <i className="ri-notification-3-line text-xl"></i>
                                {pendingCount > 0 && (
                                    <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                                        {pendingCount}
                                    </span>
                                )}
                            </button>

                            {/* Notifications Dropdown */}
                            {isNotificationsOpen && (
                                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg py-1 border border-gray-100 z-50 animate-in fade-in slide-in-from-top-2 max-h-96 overflow-y-auto">
                                    <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                        <span className="font-semibold text-sm text-gray-700">Notificações</span>
                                        <button
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                    await api.put('/api/notificacoes/read-all');
                                                    setNotifications(notifications.map((n: any) => ({ ...n, lida: true })));
                                                } catch (err) { }
                                            }}
                                            className="text-xs text-teal-600 hover:underline"
                                        >
                                            Marcar todas como lidas
                                        </button>
                                    </div>

                                    {notifications.length === 0 ? (
                                        <div className="p-4 text-center text-gray-500 text-sm">Nenhuma notificação recente</div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {notifications.map((n: any) => (
                                                <div key={n.id} className={`p-4 hover:bg-gray-50 transition-colors ${!n.lida ? 'bg-blue-50/30' : ''}`}>
                                                    <div className="flex gap-3">
                                                        <div className={`mt-1 flex-shrink-0 w-2 h-2 rounded-full ${!n.lida ? 'bg-blue-500' : 'bg-transparent'}`}></div>
                                                        <div className="flex-1">
                                                            <p className={`text-sm ${!n.lida ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>{n.titulo}</p>
                                                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.mensagem}</p>
                                                            <div className="flex justify-between items-center mt-2">
                                                                <span className="text-[10px] text-gray-400">
                                                                    {new Date(n.created_at).toLocaleString()}
                                                                </span>
                                                                {n.link && (
                                                                    <button
                                                                        onClick={() => {
                                                                            navigate(n.link);
                                                                            setIsNotificationsOpen(false);
                                                                        }}
                                                                        className="text-xs text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1"
                                                                    >
                                                                        Ver
                                                                        <i className="ri-arrow-right-line"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* User Info & Dropdown */}
                        <div className="relative" ref={menuRef}>
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="flex items-center space-x-2 px-2 py-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer rounded-lg hover:bg-gray-50"
                            >
                                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-bold text-white">{getInitials(user?.nome || '')}</span>
                                </div>
                                <div className="text-left hidden sm:block">
                                    <p className="text-sm font-medium text-gray-900">{user?.nome || 'Usuário'}</p>
                                    <p className="text-xs text-gray-500">{user?.nivel_acesso ? formatRole(user.nivel_acesso) : 'Nível'}</p>
                                </div>
                                <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}></i>
                            </button>

                            {/* Dropdown Menu */}
                            {isMenuOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 border border-gray-100 animate-in fade-in slide-in-from-top-2">
                                    <button
                                        onClick={() => {
                                            navigate('/configuracoes');
                                            setIsMenuOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                        <i className="ri-settings-4-line text-gray-400"></i>
                                        <span>Configurações</span>
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                    >
                                        <i className="ri-logout-box-line"></i>
                                        <span>Sair</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
